// Pure data constants + types for QuoteWizard. No React/client code here - // this file is safely importable from both server and client.

import type { LeadPriority, LeadStartTiming } from "@/generated/prisma/enums";

export const AUDIENCES = [
  { value: "privat", label: "Privat" },
  { value: "erhverv", label: "Erhverv" },
] as const;
export type Audience = (typeof AUDIENCES)[number]["value"];

export type PrivatTask = "lobende" | "engangs";
export type BusinessTask = "lobende" | "engangs";

// Window cleaning has been shelved as an option on both sides: it could be
// requested here but not selected as a partner service, so leads couldn't be
// matched. The value lives on in ENGANGS_SUBTYPE_TO_SERVICE + label mappings
// so historical leads can still be read.
export type OneTimeType =
  | "almindelig"
  | "grundrengoering"
  | "flytterengoering"
  | "doedsbo"
  | "efter-haandvaerker"
  | "andet-engangs";

export type BusinessOneTimeType = "hovedrengoring" | "efter-haandvaerker" | "fraflytning" | "andet";

export type BusinessType =
  | "kontor"
  | "butik"
  | "klinik"
  | "industri"
  | "institution"
  | "boligforening"
  | "andet";

export type PrivatFrequency = "ugentligt" | "hver-14-dag" | "maanedligt" | "efter-behov";
export type BusinessFrequency =
  | "dagligt"
  | "2-3x-ugentligt"
  | "ugentligt"
  | "hver-14-dag"
  | "maanedligt";
export type PropertyType = "hus" | "lejlighed" | "raekkehus" | "sommerhus";
export type TimeSlot =
  | "08-10"
  | "10-12"
  | "12-14"
  | "14-16"
  | "16-18"
  | "fleksibel"
  | "uden-aabningstid"
  | "dag"
  | "aften"
  | "weekend";

// Tracks which fields BBR has prefilled - so we can show a "from BBR" hint
// and know whether the m² warning should appear on the erhverv flow.
export type BbrPrefilled = {
  propertyType: boolean;
  floor: boolean;
  sqm: boolean;
  business: boolean;
};

export type State = {
  audience: Audience | null;
  task: PrivatTask | null;
  businessTask: BusinessTask | null;
  business: BusinessType | null;
  address: import("@/features/geo/shared/address-types").AddressSuggestion | null;
  // User chose to skip the address lookup (or the BBR lookup failed) - we
  // then show only the manual fields (property type, floor, m², postal code).
  addressSkipped: boolean;
  streetAddress: string;
  bbrPrefilled: BbrPrefilled;
  propertyType: PropertyType | "";
  floor: string;
  squareMeters: string;
  frequency: string;
  engangsSubtype: string;
  engangsAndetDescription: string;
  startTiming: LeadStartTiming | "";
  // yyyy-mm-dd from the date input, only filled when startTiming is SPECIFIK_DATO.
  startDate: string;
  timeSlots: TimeSlot[];
  preferences: string[];
  priority: LeadPriority | "";
  postalCode: string;
  notes: string;
  contactName: string;
  companyName: string;
  email: string;
  phone: string;
  consent: boolean;
};

// Contact fields are deliberately absent: they are always typed on the page.
export type InitialFields = Pick<
  State,
  | "audience"
  | "postalCode"
  | "task"
  | "business"
  | "engangsSubtype"
  | "frequency"
  | "squareMeters"
  | "propertyType"
  | "floor"
  | "startTiming"
  | "startDate"
  | "timeSlots"
  | "preferences"
  | "notes"
>;

export const PRIVAT_TASKS: { value: PrivatTask; label: string; body: string; icon: string }[] = [
  {
    value: "lobende",
    label: "Løbende rengøring",
    body: "Fast aftale, fx hver uge eller hver 14. dag.",
    icon: "/images/lineboil/12-trappemoppe-mini.webp",
  },
  {
    value: "engangs",
    label: "Engangsrengøring",
    body: "Én opgave. Fx hovedrengøring, flytning eller dødsbo.",
    icon: "/images/lineboil/05-spray-mini.webp",
  },
];

export const ONE_TIME_TYPES: { value: OneTimeType; label: string; body: string; icon: string }[] = [
  {
    value: "almindelig",
    label: "Almindelig rengøring",
    body: "Helt normal rengøring én gang - fx hvis din faste hjælper er forhindret.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    // Value stays `grundrengoering` (maps to the "Grundrengøring" service in
    // ENGANGS_SUBTYPE_TO_SERVICE) - only the label changes, since
    // "Hovedrengøring" is the word people actually use.
    value: "grundrengoering",
    label: "Hovedrengøring",
    body: "Grundig gennemgang, inkl. ovn, skabe og vinduer.",
    icon: "/images/lineboil/15-checkmark-mini.webp",
  },
  {
    value: "flytterengoering",
    label: "Flytterengøring",
    body: "Ved fraflytning eller overdragelse. Typisk med garanti.",
    icon: "/images/lineboil/01-flyttekasser-mini.webp",
  },
  {
    value: "doedsbo",
    label: "Dødsbo",
    body: "Boligklargøring efter dødsfald.",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  {
    value: "efter-haandvaerker",
    label: "Efter håndværker",
    body: "Rengøring efter renovering eller byggerod.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    value: "andet-engangs",
    label: "Andet",
    body: "Beskriv selv hvad der skal laves.",
    icon: "",
  },
];

export const BUSINESS_TYPES: {
  value: BusinessType;
  label: string;
  icon: string;
}[] = [
  { value: "kontor", label: "Kontor", icon: "/images/lineboil/02-kontor-mini.webp" },
  {
    value: "butik",
    label: "Butik / restaurant",
    icon: "/images/lineboil/13-cafe-mini.webp",
  },
  {
    value: "klinik",
    label: "Klinik / sundhed",
    icon: "/images/lineboil/14-klinik-mini.webp",
  },
  {
    value: "industri",
    label: "Industri / lager",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  {
    value: "institution",
    label: "Institution",
    icon: "/images/lineboil/14-klinik-mini.webp",
  },
  {
    value: "boligforening",
    label: "Boligforening",
    icon: "/images/lineboil/04-etageejendom-mini.webp",
  },
  { value: "andet", label: "Andet", icon: "" },
];

export const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: "lejlighed", label: "Lejlighed" },
  { value: "hus", label: "Hus" },
  { value: "raekkehus", label: "Rækkehus" },
  { value: "sommerhus", label: "Sommerhus" },
];

export const FLOORS: { value: string; label: string }[] = [
  { value: "stuen", label: "Stuen" },
  { value: "1", label: "1. sal" },
  { value: "2", label: "2. sal" },
  { value: "3", label: "3. sal" },
  { value: "4-plus", label: "4. sal eller højere" },
];

export const PRIVAT_FREQUENCIES: { value: PrivatFrequency; label: string }[] = [
  { value: "ugentligt", label: "Hver uge" },
  { value: "hver-14-dag", label: "Hver 14. dag" },
  { value: "maanedligt", label: "Hver måned" },
  { value: "efter-behov", label: "Efter behov" },
];

export const BUSINESS_FREQUENCIES: { value: BusinessFrequency; label: string }[] = [
  { value: "dagligt", label: "Hver dag" },
  { value: "2-3x-ugentligt", label: "Flere gange/uge" },
  { value: "ugentligt", label: "Hver uge" },
  { value: "hver-14-dag", label: "Hver 14. dag" },
  { value: "maanedligt", label: "Hver måned" },
];

export const BUSINESS_TASKS: { value: BusinessTask; label: string; body: string; icon: string }[] =
  [
    {
      value: "lobende",
      label: "Løbende aftale",
      body: "Fast aftale, fx flere gange om ugen eller hver 14. dag.",
      icon: "/images/lineboil/12-trappemoppe-mini.webp",
    },
    {
      value: "engangs",
      label: "Engangsopgave",
      body: "Én opgave. Fx hovedrengøring eller byggerengøring.",
      icon: "/images/lineboil/05-spray-mini.webp",
    },
  ];

export const BUSINESS_ONE_TIME_TYPES: {
  value: BusinessOneTimeType;
  label: string;
  body: string;
  icon: string;
}[] = [
  {
    value: "hovedrengoring",
    label: "Hovedrengøring",
    body: "Grundig gennemgang af jeres lokaler.",
    icon: "/images/lineboil/15-checkmark-mini.webp",
  },
  {
    value: "efter-haandvaerker",
    label: "Efter håndværker",
    body: "Byggerengøring efter renovering eller ombygning.",
    icon: "/images/lineboil/18-moppe.webp",
  },
  {
    value: "fraflytning",
    label: "Fraflytning",
    body: "Når I opsiger lejekontrakten på lokalerne.",
    icon: "/images/lineboil/01-flyttekasser-mini.webp",
  },
  {
    value: "andet",
    label: "Andet",
    body: "Beskriv selv hvad der skal laves.",
    icon: "",
  },
];

export const BUSINESS_TIME_SLOTS: { value: TimeSlot; label: string }[] = [
  { value: "uden-aabningstid", label: "Uden for åbningstid" },
  { value: "dag", label: "Dag (08–16)" },
  { value: "aften", label: "Aften (16–22)" },
  { value: "weekend", label: "Weekend" },
  { value: "fleksibel", label: "Fleksibel" },
];

export const TIME_SLOTS: { value: TimeSlot; label: string }[] = [
  { value: "08-10", label: "Kl. 08–10" },
  { value: "10-12", label: "Kl. 10–12" },
  { value: "12-14", label: "Kl. 12–14" },
  { value: "14-16", label: "Kl. 14–16" },
  { value: "16-18", label: "Kl. 16–18" },
  { value: "fleksibel", label: "Fleksibel" },
];

// Every value must be one matchCompanyPreferences (preference-matching.ts)
// knows, or the wish silently counts for nothing in the ranking.
export const PRIVAT_PREFERENCES = [
  { value: "dyrevenlig", label: "Allergivenlige midler" },
  { value: "husdyr", label: "Erfaring med husdyr" },
  { value: "fast-person", label: "Samme person hver gang" },
  { value: "medbragt-midler", label: "Firmaet medbringer udstyr" },
];

export const BUSINESS_PREFERENCES = [
  // Every value must be one matchCompanyPreferences (preference-matching.ts)
  // knows, or the wish silently counts for nothing in the ranking.
  { value: "svanemaerket", label: "Svanemærket rengøring" },
  { value: "overenskomst", label: "Medarbejdere på overenskomst" },
  { value: "samme-team", label: "Samme team hver gang" },
  { value: "fast-kontakt", label: "Fast kontaktperson" },
  { value: "dansktalende", label: "Dansktalende personale" },
  { value: "straffeattest", label: "Straffeattest på personale" },
];

type OptionSet = {
  tasks: readonly { value: PrivatTask; label: string; body: string; icon: string }[];
  subtypes: readonly { value: string; label: string; body: string; icon: string }[];
  frequencies: readonly { value: string; label: string }[];
  timeSlots: readonly { value: TimeSlot; label: string }[];
  preferences: readonly { value: string; label: string }[];
};

// Shared by query parsing and normalize so both accept the same answers.
const AUDIENCE_OPTIONS: Record<Audience, OptionSet> = {
  privat: {
    tasks: PRIVAT_TASKS,
    subtypes: ONE_TIME_TYPES,
    frequencies: PRIVAT_FREQUENCIES,
    timeSlots: TIME_SLOTS,
    preferences: PRIVAT_PREFERENCES,
  },
  erhverv: {
    tasks: BUSINESS_TASKS,
    subtypes: BUSINESS_ONE_TIME_TYPES,
    frequencies: BUSINESS_FREQUENCIES,
    timeSlots: BUSINESS_TIME_SLOTS,
    preferences: BUSINESS_PREFERENCES,
  },
};

// Until a link or the audience step names an audience, both lists apply.
// Privat comes first so the same input always yields the same order.
export function optionListsFor(audience: Audience | null): OptionSet {
  const sets =
    audience === null
      ? [AUDIENCE_OPTIONS.privat, AUDIENCE_OPTIONS.erhverv]
      : [AUDIENCE_OPTIONS[audience]];
  return {
    tasks: sets.flatMap((s) => s.tasks),
    subtypes: sets.flatMap((s) => s.subtypes),
    frequencies: sets.flatMap((s) => s.frequencies),
    timeSlots: sets.flatMap((s) => s.timeSlots),
    preferences: sets.flatMap((s) => s.preferences),
  };
}

const EMPTY_STATE: State = {
  audience: null,
  task: null,
  businessTask: null,
  business: null,
  address: null,
  addressSkipped: false,
  streetAddress: "",
  bbrPrefilled: { propertyType: false, floor: false, sqm: false, business: false },
  propertyType: "",
  floor: "",
  squareMeters: "",
  frequency: "",
  engangsSubtype: "",
  engangsAndetDescription: "",
  startTiming: "",
  startDate: "",
  timeSlots: [],
  preferences: [],
  priority: "",
  postalCode: "",
  notes: "",
  contactName: "",
  companyName: "",
  email: "",
  phone: "",
  consent: false,
};

// `task` is one answer for both wizards until the audience is known; normalize
// keeps the copy the chosen audience can show.
export function initialState(init: Partial<InitialFields> = {}): State {
  const { task = null, ...rest } = init;
  return normalize({ ...EMPTY_STATE, ...rest, task, businessTask: task });
}

// Runs on every write to wizard state, so no step has to clear dependent
// answers by hand and a link can't carry more than a click could.
export function normalize(state: State): State {
  const { audience } = state;
  const lists = optionListsFor(audience);
  const has = (options: readonly { value: string }[], value: string) =>
    options.some((o) => o.value === value);

  const task = audience === "erhverv" ? state.businessTask : state.task;
  const propertyType = audience === "erhverv" ? "" : state.propertyType;
  const engangsSubtype =
    task === "engangs" && has(lists.subtypes, state.engangsSubtype) ? state.engangsSubtype : "";

  return {
    ...state,
    task: audience === "erhverv" ? null : state.task,
    businessTask: audience === "privat" ? null : state.businessTask,
    business: audience === "privat" ? null : state.business,
    propertyType,
    floor: propertyType === "lejlighed" ? state.floor : "",
    frequency: task === "lobende" && has(lists.frequencies, state.frequency) ? state.frequency : "",
    engangsSubtype,
    engangsAndetDescription:
      audience === "erhverv" && engangsSubtype === "andet" ? state.engangsAndetDescription : "",
    startDate: state.startTiming === "SPECIFIK_DATO" ? state.startDate : "",
    timeSlots: state.timeSlots.filter((value) => has(lists.timeSlots, value)),
    preferences: state.preferences.filter((value) => has(lists.preferences, value)),
  };
}

export function selectAudience(state: State, audience: Audience): State {
  return normalize({ ...state, audience });
}
